import React, { useState } from "react";
import { normalizeRecoveryCode } from "../../lib/numberSenseData";
import { Icon, Keys } from "../numberSenseUi";

/* ============================================================
   Number Sense — "carry your progress" / cross-device recovery.
   Progress is saved per-device under an anonymous code (the
   Firestore user id). This screen surfaces that code to copy, and
   accepts a code pasted from another device to re-bind this device
   to that account. Degrades gracefully when the cloud isn't set up.

   Props:
     code      : string   this device's recovery code (user id)
     isCloud   : boolean  whether Firestore sync is configured
     onBack    : () => void
     onRestore : (code) => void   re-bind to a pasted code
   ============================================================ */
const SyncScreen = ({ code, isCloud, onBack, onRestore }) => {
  const [entry, setEntry] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("couldn't copy automatically — select the code and copy it.");
    }
  };

  const restore = () => {
    const clean = normalizeRecoveryCode(entry);
    if (!clean) {
      setError("that doesn't look like a valid code.");
      return;
    }
    if (clean === code) {
      setError("that's already this device's code.");
      return;
    }
    onRestore(clean);
  };

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onBack} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">carry your progress</div>
        </div>
        <div className="scroll">
          {isCloud ? (
            <>
              <p className="games-intro">
                Your XP, streak and lessons are saved to the cloud under the code
                below. Copy it somewhere safe, then paste it on another device to
                pick up exactly where you left off.
              </p>

              <div className="ns-sync-block">
                <div className="ns-sync-h">this device's code</div>
                <div className="ns-code">
                  <code>{code}</code>
                  <button className="btn" onClick={copy}>
                    {copied ? "copied!" : "copy"}
                  </button>
                </div>
              </div>

              <div className="ns-sync-block">
                <div className="ns-sync-h">restore from another device</div>
                <p className="ns-stat-note">
                  Paste a code to load that account here. Your current code stays
                  valid — keep it if you want to switch back.
                </p>
                <div className="ns-code-entry">
                  <input
                    value={entry}
                    onChange={(e) => {
                      setEntry(e.target.value);
                      setError("");
                    }}
                    placeholder="paste a recovery code"
                    aria-label="recovery code"
                  />
                  <button
                    className="btn loje"
                    disabled={!entry.trim()}
                    onClick={restore}
                  >
                    restore
                  </button>
                </div>
                {error && <div className="ns-sync-err">{error}</div>}
              </div>
            </>
          ) : (
            <div className="ns-stats-empty">
              <Keys />
              <p>
                cloud sync isn't set up on this site, so your progress is saved
                only in this browser. There's no portable code yet — once cloud
                sync is configured, you'll be able to carry your progress between
                devices from here.
              </p>
            </div>
          )}
          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyncScreen;
